import {
	DataGrid,
	GridFooter,
	GridToolbar,
	gridStringOrNumberComparator,
} from '@mui/x-data-grid';
import { useQuery } from 'react-query';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import { useContext, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { getServiceLocations } from '../../../../../../../data/promisApiCalls';
import CustomLoading from '../../../../../../shared/CustomLoading';
import { AuthSubmissionContext } from '../../AuthSubmissionContext';
import { formatAddress } from '../../../../../../../scripts/addressHelper';

const SelectServiceLocation = ({ practitionerId = null, onSelect }) => {
	const { sessionProviderId, sessionPractitionerId } = useContext(
		AuthSubmissionContext
	);

	const [selectionModel, setSelectionModel] = useState([]);
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});

	const { setError } = useContext(AuthSubmissionContext);

	const columns = [
		{
			field: 'id',
			headerName: 'ID',
			flex: 1,
		},
		{
			field: 'selected',
			headerName: 'Select',
			sortable: false,
			renderCell: e => (
				<Radio
					checked={
						(selectionModel[0]?.id === e.row.id &&
							selectionModel[0]?.taxId === e.row.taxId &&
							selectionModel[0]?.providerDocSuffix ===
								e.row.providerDocSuffix &&
							selectionModel[0]?.practitionerDocSuffix ===
								e.row.practitionerDocSuffix) ??
						false
					}
					value={e.id}
					inputProps={{
						'aria-label': `${e.row.clinicName} at ${e.row.address1}`,
					}}
				/>
			),
		},
		{
			field: 'name',
			headerName: 'Clinic',
			flex: 2,
			minWidth: 300,
			valueGetter: (value, row) => ({
				name: row.name,
				address: formatAddress(
					row.address1,
					row.address2,
					row.city,
					row.state,
					row.zipCode
				),
			}),
			valueFormatter: value => value.name,
			sortComparator: (v1, v2, p1, p2) =>
				gridStringOrNumberComparator(
					`${v1.name} ${v1.address}`,
					`${v2.name} ${v2.address}`,
					p1,
					p2
				),
			getApplyQuickFilterFn: value => (cellValue, row) =>
				row.name.toLowerCase().includes(value.toLowerCase()),
		},
		{
			field: 'address',
			headerName: 'Address',
			flex: 4,
			minWidth: 300,
			valueGetter: (value, row) => ({
				name: row.name,
				address: formatAddress(
					row.address1,
					row.address2,
					row.city,
					row.state,
					row.zipCode
				),
			}),
			valueFormatter: value => value.address,
			sortComparator: (v1, v2, p1, p2) =>
				gridStringOrNumberComparator(
					`${v1.address} ${v1.name}`,
					`${v2.address} ${v2.name}`,
					p1,
					p2
				),
			getApplyQuickFilterFn: value => cellValue =>
				cellValue.address.toLowerCase().includes(value.toLowerCase()),
		},
		{
			field: 'effectiveDate',
			headerName: 'Effective',
			flex: 1,
			minWidth: 100,
		},
		{ field: 'termDate', headerName: 'Termed', flex: 1, minWidth: 100 },
	];

	const { isLoading, isError, data } = useQuery(
		['getServiceLocations', practitionerId],
		async () =>
			getServiceLocations(
				sessionProviderId,
				practitionerId ?? sessionPractitionerId
			),
		{ staleTime: Infinity }
	);

	const { data: { resultCode, data: { serviceLocations = [] } = {} } = {} } =
		data ?? {};

	// https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose#defining-on-demand-messages
	useEffect(() => {
		if (isError || (data && resultCode !== 0)) {
			setError('There was a problem getting service locations');
			window.scrollTo({
				top: 0,
				left: 0,
			});
		}
	}, [isError, data]);

	if (isLoading) {
		return <CustomLoading />;
	}
	if (isError || (data && resultCode !== 0)) {
		// https://medium.com/@davidkelley87/stop-using-return-null-in-react-a2ebf08fc9cd
		return false;
	}

	return (
		<Box>
			<Typography fontWeight='bold'>
				Select Service Location for this E-MNR:
			</Typography>
			<DataGrid
				autoHeight
				rows={serviceLocations ?? []}
				loading={isLoading}
				columns={columns}
				columnVisibilityModel={{
					id: false,
				}}
				getRowId={row =>
					row.id +
					row.taxId +
					row.providerDocSuffix +
					row.practitionerDocSuffix
				}
				slots={{
					loadingOverlay: CustomLoading,
					footer: GridFooter,
					toolbar: GridToolbar,
				}}
				slotProps={{
					toolbar: {
						showQuickFilter: serviceLocations.length > 0,
						printOptions: {
							disableToolbarButton: true,
						},
						csvOptions: { disableToolbarButton: true },
						quickFilterProps: { debounceMs: 1000 },
					},
				}}
				pagination
				showToolbar
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				pageSizeOptions={[10, 20, 50]}
				hideFooterSelectedRowCount
				disableMultipleRowSelection
				disableColumnMenu
				disableColumnFilter
				disableColumnResize
				disableColumnSelector
				disableDensitySelector
				disableRowSelectionOnClick
				onCellKeyDown={(p, e) => {
					if (e.code === 'Space') {
						e.preventDefault();
						e.stopPropagation();
						const newSelectionModel = serviceLocations.find(
							x =>
								x.id +
									x.taxId +
									x.providerDocSuffix +
									x.practitionerDocSuffix ===
								p.id
						);
						setSelectionModel([newSelectionModel]);
						onSelect(newSelectionModel);
					}
				}}
				onCellClick={p => {
					const newSelectionModel = serviceLocations.find(
						x =>
							x.id +
								x.taxId +
								x.providerDocSuffix +
								x.practitionerDocSuffix ===
							p.id
					);
					setSelectionModel([newSelectionModel]);
					onSelect(newSelectionModel);
				}}
				initialState={{
					sorting: {
						sortModel: [{ field: 'clinicName', sort: 'asc' }],
					},
				}}
				sortingOrder={['asc', 'desc']}
			/>
			{selectionModel?.length === 1 && (
				<Typography>{`Selected: ${
					selectionModel[0].name
				} at ${formatAddress(
					selectionModel[0].address1,
					selectionModel[0].address2,
					selectionModel[0].city,
					selectionModel[0].state,
					selectionModel[0].zipCode
				)}`}</Typography>
			)}
		</Box>
	);
};

SelectServiceLocation.propTypes = {
	practitionerId: PropTypes.number,
	onSelect: PropTypes.func.isRequired,
};

export default SelectServiceLocation;
